import {
  CHUNK_X, CHUNK_Z, DAY_LENGTH, MS_PER_TICK, WORLD_MAX_Y, WORLD_MIN_Y,
  chunkKey, chunkKeyX, chunkKeyZ, type DimensionId,
} from '../shared/constants.ts';
import { blockNameOf, blockOf, faceTexture, stateOf } from '../shared/registry/blocks.ts';
import { Dir } from '../shared/registry/direction.ts';
import { collisionBoxes } from '../shared/registry/shapes.ts';
import { itemById } from '../shared/registry/items.ts';
import { miningInfo } from '../shared/mining.ts';
import { raycast, tickPlayerPhysics, type MoveInput, type PhysicsEntity } from '../shared/physics/physics.ts';
import type { NetItemStack } from '../shared/net/protocol.ts';
import { ClientWorld } from './world.ts';
import { MesherPool } from './mesher/pool.ts';
import { Renderer } from './render/renderer.ts';
import { NetClient, type EntityInfo } from './net.ts';
import { Hud, prettyName, type HudState, type WindowState } from './ui/hud.ts';
import { SoundEngine } from './sound.ts';

/**
 * Client entry point.
 *
 * Runs a fixed 20 Hz simulation for the local player (using exactly the shared
 * physics the server runs, so predicted positions match), renders at display
 * refresh with interpolation between simulation steps, and streams chunk
 * meshing out to a worker pool.
 */

const PLAYER_HEIGHT = 1.8;
const PLAYER_EYE = 1.62;
const PLAYER_WIDTH = 0.6;
const REACH = 5.0;
/** Pixels the pointer must travel before a press counts as a look-drag. */
const DRAG_LOOK_THRESHOLD = 5;
/** Milliseconds a still press must be held before it starts mining. */
const DRAG_HOLD_TO_MINE_MS = 200;

interface RemoteEntity extends EntityInfo {
  /** Interpolation source and target, so movement is smooth between updates. */
  prevX: number;
  prevY: number;
  prevZ: number;
  targetX: number;
  targetY: number;
  targetZ: number;
  lerpStart: number;
  hurt: boolean;
}

class Game {
  readonly world = new ClientWorld();
  readonly renderer: Renderer;
  readonly hud: Hud;
  readonly pool = new MesherPool();
  readonly sound = new SoundEngine();
  net!: NetClient;

  playerId = 0;
  dimension: DimensionId = 'overworld';
  player: PhysicsEntity & { yaw: number; pitch: number } = {
    x: 0, y: 80, z: 0,
    vx: 0, vy: 0, vz: 0,
    width: PLAYER_WIDTH, height: PLAYER_HEIGHT,
    onGround: false, climbing: false, inWater: false, inLava: false,
    fallDistance: 0, flying: false,
    yaw: 0, pitch: 0,
  };
  gameMode = 0;
  sneaking = false;
  sprinting = false;

  hudState: HudState = {
    health: 20, maxHealth: 20, food: 20, air: 10,
    xpLevel: 0, xpProgress: 0, gameMode: 0,
    hotbar: new Array(9).fill(null), selectedSlot: 0, dead: false,
    itemNameFlash: null,
  };
  inventory: (NetItemStack | null)[] = new Array(41).fill(null);
  windowState: WindowState = { open: false, id: 0, kind: 'inventory', slots: [], carried: null, furnace: null };
  inventoryOpen = false;

  worldTime = 0;
  serverTick = 0;
  private timeAtLastSync = 0;

  readonly entities = new Map<number, RemoteEntity>();
  /** Break animation stages from other players, keyed by "x,y,z". */
  readonly breakStages = new Map<string, number>();

  // Input
  readonly keys = new Set<string>();
  pointerLocked = false;
  /** Set once the "capture unavailable" hint has been shown, so it is shown once. */
  pointerLockHintShown = false;
  /**
   * Fallback look mode, used until pointer lock engages: hold the left button
   * and drag to turn, hold it still to mine.
   */
  dragLooking = false;
  dragMoved = false;
  dragLastX = 0;
  dragLastY = 0;
  dragStartX = 0;
  dragStartY = 0;
  dragStartedAt = 0;
  mining = false;
  miningTarget: { x: number; y: number; z: number } | null = null;
  miningTicks = 0;
  placeCooldown = 0;
  target: { x: number; y: number; z: number; face: number; hitX: number; hitY: number; hitZ: number } | null = null;

  // Timing
  private accumulator = 0;
  private lastFrame = 0;
  private lastFps = 0;
  private frames = 0;
  private fps = 0;
  private renderDistance = 8;
  private meshBudgetPerFrame = 3;
  private connectionLost = '';

  constructor(canvas: HTMLCanvasElement, hudCanvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas);
    this.hud = new Hud(hudCanvas);
  }

  async start(): Promise<void> {
    await this.renderer.init();
    await this.hud.loadAtlases();
    this.pool.setHandler((result) => {
      // Drop results for chunks that changed again while meshing.
      const current = this.world.revisions.get(chunkKey(result.cx, result.cz)) ?? -1;
      if (!this.world.hasChunk(result.cx, result.cz)) return;
      if (result.revision < current) {
        this.world.dirty.add(chunkKey(result.cx, result.cz));
        return;
      }
      this.renderer.uploadMesh(result);
    });

    this.setupInput();
    this.connect();
    this.lastFrame = performance.now();
    requestAnimationFrame(this.frame);
  }

  private connect(): void {
    const params = new URLSearchParams(location.search);
    const name = params.get('name') ?? localStorage.getItem('opuscraft:name') ?? `Player${Math.floor(Math.random() * 900 + 100)}`;
    localStorage.setItem('opuscraft:name', name);
    let uuid = localStorage.getItem('opuscraft:uuid');
    if (!uuid) {
      uuid = crypto.randomUUID();
      localStorage.setItem('opuscraft:uuid', uuid);
    }
    const saved = Number(localStorage.getItem('opuscraft:renderDistance'));
    if (Number.isFinite(saved) && saved >= 2) this.renderDistance = Math.min(24, saved);

    this.net = new NetClient({
      onHello: (info) => {
        this.playerId = info.playerId;
        this.dimension = info.dimension;
        this.world.dimension = info.dimension;
        this.player.x = info.x;
        this.player.y = info.y;
        this.player.z = info.z;
        this.player.yaw = info.yaw;
        this.player.pitch = info.pitch;
        this.worldTime = info.time;
        this.timeAtLastSync = performance.now();
        this.hud.addChat(`Connected as ${info.name} (seed ${info.seed})`);
        this.net.sendRenderDistance(this.renderDistance);
      },
      onChunk: (chunk) => {
        this.world.setChunk(chunk);
      },
      onChunkUnload: (cx, cz) => {
        this.world.removeChunk(cx, cz);
        this.renderer.deleteMesh(chunkKey(cx, cz));
      },
      onBlockChange: (x, y, z, state) => {
        this.world.setBlock(x, y, z, state);
      },
      onMultiBlockChange: (_cx, _cz, changes) => {
        for (const c of changes) this.world.setBlock(c.x, c.y, c.z, c.state);
      },
      onLight: (cx, cz, sections) => {
        const chunk = this.world.getChunk(cx, cz);
        if (!chunk) return;
        for (const s of sections) {
          const section = chunk.sections[s.sy] ?? chunk.getOrCreateSection(s.sy);
          section.skyLight.set(s.sky);
          section.blockLight.set(s.block);
        }
        this.world.markDirtyWithNeighbors(cx, cz);
      },
      onTime: (time, tick) => {
        this.worldTime = time;
        this.serverTick = tick;
        this.timeAtLastSync = performance.now();
      },
      onPlayerState: (s) => {
        this.hudState.health = s.health;
        this.hudState.food = s.food;
        this.hudState.air = s.air;
        this.hudState.xpLevel = s.xpLevel;
        this.hudState.xpProgress = s.xpProgress;
        this.hudState.dead = s.dead;
        this.hudState.gameMode = s.gameMode;
        this.gameMode = s.gameMode;
        this.player.flying = s.gameMode !== 0 && this.player.flying;
      },
      onPositionCorrection: (x, y, z, yaw, pitch) => {
        this.player.x = x;
        this.player.y = y;
        this.player.z = z;
        this.player.vx = 0;
        this.player.vy = 0;
        this.player.vz = 0;
        // The view angles are deliberately left alone: the server only ever
        // echoes back what this client reported, so applying them here would
        // fight with whatever the player is currently doing with the mouse.
      },
      onInventory: (slots, selected, carried) => {
        this.inventory = slots;
        this.hudState.selectedSlot = selected;
        for (let i = 0; i < 9; i++) this.hudState.hotbar[i] = slots[i] ?? null;
        this.windowState.carried = carried;
      },
      onEntitySpawn: (info) => {
        this.entities.set(info.id, {
          ...info,
          prevX: info.x, prevY: info.y, prevZ: info.z,
          targetX: info.x, targetY: info.y, targetZ: info.z,
          lerpStart: performance.now(),
          hurt: false,
        });
      },
      onEntityUpdate: (id, x, y, z, yaw, pitch, health, hurt) => {
        const e = this.entities.get(id);
        if (!e) return;
        e.prevX = e.targetX;
        e.prevY = e.targetY;
        e.prevZ = e.targetZ;
        e.targetX = x;
        e.targetY = y;
        e.targetZ = z;
        e.yaw = yaw;
        e.pitch = pitch;
        e.health = health;
        e.hurt = hurt;
        e.lerpStart = performance.now();
      },
      onEntityRemove: (id) => {
        this.entities.delete(id);
      },
      onChat: (message) => this.hud.addChat(message),
      onOpenContainer: (info) => {
        this.windowState = { open: true, id: info.id, kind: info.kind, slots: [], carried: null, furnace: null };
        this.inventoryOpen = false;
        this.releasePointer();
      },
      onContainerUpdate: (id, slots, carried, furnace) => {
        if (this.windowState.id !== id) return;
        this.windowState.slots = slots;
        this.windowState.carried = carried;
        this.windowState.furnace = furnace;
      },
      onCloseContainer: () => {
        this.windowState.open = false;
      },
      onSound: (name, x, y, z, volume, pitch) => {
        this.sound.play(name, x, y, z, volume, pitch, this.player.x, this.player.y, this.player.z);
      },
      onParticle: () => {},
      onDimension: (dimension, x, y, z) => {
        this.dimension = dimension;
        this.world.dimension = dimension;
        this.world.clear();
        this.renderer.clearMeshes();
        this.pool.clear();
        this.entities.clear();
        this.player.x = x;
        this.player.y = y;
        this.player.z = z;
        this.player.vx = this.player.vy = this.player.vz = 0;
        this.hud.addChat(`Entered the ${dimension.replace('_', ' ')}`);
      },
      onBreakProgress: (playerId, x, y, z, stage) => {
        if (playerId === this.playerId) return;
        const key = `${x},${y},${z}`;
        if (stage >= 9) this.breakStages.delete(key);
        else this.breakStages.set(key, stage);
      },
      onRespawnInfo: () => {},
      onBossBar: (boss) => {
        this.hud.boss = boss;
      },
      onDisconnect: (reason) => {
        this.connectionLost = reason;
        this.hud.addChat(`Disconnected: ${reason}`);
      },
    });

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = params.get('server') ?? `${protocol}//${location.host}/ws`;
    this.net.connect(url, name, uuid);
  }

  // -------------------------------------------------------------------------
  // Input
  // -------------------------------------------------------------------------

  private setupInput(): void {
    const canvas = this.renderer.canvas;

    document.addEventListener('keydown', (ev) => {
      if (this.hud.chatInput !== null) {
        ev.preventDefault();
        if (ev.key === 'Enter') {
          const text = this.hud.chatInput.trim();
          if (text) this.net.sendChat(text);
          this.hud.chatInput = null;
        } else if (ev.key === 'Escape') {
          this.hud.chatInput = null;
        } else if (ev.key === 'Backspace') {
          this.hud.chatInput = this.hud.chatInput.slice(0, -1);
        } else if (ev.key.length === 1) {
          this.hud.chatInput += ev.key;
        }
        return;
      }

      this.keys.add(ev.code);
      switch (ev.code) {
        case 'KeyE':
          this.toggleInventory();
          ev.preventDefault();
          break;
        case 'Escape':
          if (this.windowState.open) {
            this.net.sendCloseContainer();
            this.windowState.open = false;
          } else if (this.inventoryOpen) {
            this.inventoryOpen = false;
          }
          break;
        case 'KeyT':
        case 'Slash':
          this.hud.chatInput = ev.code === 'Slash' ? '/' : '';
          this.releasePointer();
          ev.preventDefault();
          break;
        case 'F3':
          this.hud.debugVisible = !this.hud.debugVisible;
          ev.preventDefault();
          break;
        case 'KeyQ':
          this.net.sendDrop(ev.ctrlKey);
          break;
        case 'KeyR':
          if (this.hudState.dead) this.net.sendRespawn();
          break;
        case 'KeyF':
          this.net.sendToggleFly();
          this.player.flying = !this.player.flying;
          break;
        default:
          break;
      }
      if (ev.code.startsWith('Digit')) {
        const n = Number(ev.code.slice(5));
        if (n >= 1 && n <= 9) this.selectSlot(n - 1);
      }
      if (ev.code === 'BracketLeft' || ev.code === 'BracketRight') {
        this.setRenderDistance(this.renderDistance + (ev.code === 'BracketRight' ? 1 : -1));
      }
    });

    document.addEventListener('keyup', (ev) => this.keys.delete(ev.code));

    // All mouse input is handled on the document and routed by UI state. The
    // HUD canvas covers the viewport, so binding to a specific canvas would
    // mean whichever one happens to be on top wins.
    document.addEventListener('mousedown', (ev) => {
      if (this.hud.chatInput !== null) return;

      if (this.uiOpen) {
        this.handleWindowClick(ev);
        ev.preventDefault();
        return;
      }
      if (!this.pointerLocked) {
        // Always ask for the lock; every click is a fresh user gesture, so a
        // request refused once (Chrome blocks re-locking just after Escape)
        // can still succeed on the next click.
        this.capturePointer();

        // Until the lock takes effect, the left button doubles as look-drag and
        // mine: moving past a few pixels turns the view, holding still starts
        // mining. Right and middle click keep acting immediately.
        if (ev.button === 0) {
          this.dragLooking = true;
          this.dragMoved = false;
          this.dragLastX = ev.clientX;
          this.dragLastY = ev.clientY;
          this.dragStartX = ev.clientX;
          this.dragStartY = ev.clientY;
          this.dragStartedAt = performance.now();
          ev.preventDefault();
          return;
        }
      }

      if (ev.button === 0) {
        // Attacking an entity takes priority over mining the block behind it.
        const hit = this.pickEntity();
        if (hit !== null) this.net.sendAttack(hit);
        else this.mining = true;
      }
      if (ev.button === 2) {
        const hit = this.pickEntity();
        if (hit !== null) this.net.sendInteract(hit);
        else this.usePlace();
      }
      if (ev.button === 1) this.pickBlock();
    });

    document.addEventListener('mouseup', (ev) => {
      if (ev.button === 0) {
        // A quick left click that never became a drag or a hold is an attack.
        if (this.dragLooking && !this.dragMoved && !this.mining) {
          const hit = this.pickEntity();
          if (hit !== null) this.net.sendAttack(hit);
        }
        this.mining = false;
        this.stopMining();
        this.dragLooking = false;
      }
    });

    document.addEventListener('contextmenu', (ev) => ev.preventDefault());

    document.addEventListener('mousemove', (ev) => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.hud.mouseX = ev.clientX * dpr;
      this.hud.mouseY = ev.clientY * dpr;

      const sensitivity = 0.0022;
      let dx = 0;
      let dy = 0;
      if (this.pointerLocked) {
        dx = ev.movementX;
        dy = ev.movementY;
      } else if (this.dragLooking && !this.uiOpen) {
        // Only start turning once the pointer has clearly moved, so a press
        // that was meant as a click-and-hold does not nudge the view.
        if (!this.dragMoved) {
          const moved = Math.hypot(ev.clientX - this.dragStartX, ev.clientY - this.dragStartY);
          if (moved < DRAG_LOOK_THRESHOLD) return;
          this.dragMoved = true;
        }
        dx = ev.clientX - this.dragLastX;
        dy = ev.clientY - this.dragLastY;
        this.dragLastX = ev.clientX;
        this.dragLastY = ev.clientY;
      } else {
        return;
      }

      // Yaw 0 faces +Z and the right-hand vector is -X, so turning to the
      // player's right means *increasing* yaw. Pitch is positive upward, so
      // moving the mouse down (dy > 0) looks down.
      this.player.yaw += dx * sensitivity;
      this.player.pitch -= dy * sensitivity;
      const limit = Math.PI / 2 - 0.001;
      this.player.pitch = Math.max(-limit, Math.min(limit, this.player.pitch));
    });

    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === canvas;
      if (this.pointerLocked) {
        this.dragLooking = false;
        document.body.classList.add('playing');
      } else {
        this.mining = false;
        this.keys.clear();
        document.body.classList.remove('playing');
      }
    });

    document.addEventListener('pointerlockerror', () => {
      this.onPointerLockUnavailable();
    });

    document.addEventListener('wheel', (ev) => {
      if (this.uiOpen) return;
      const dir = Math.sign(ev.deltaY);
      this.selectSlot((this.hudState.selectedSlot + dir + 9) % 9);
      ev.preventDefault();
    }, { passive: false });

    window.addEventListener('beforeunload', () => this.pool.dispose());
  }

  /** True while any inventory or container screen is showing. */
  private get uiOpen(): boolean {
    return this.inventoryOpen || this.windowState.open;
  }

  /**
   * Ask for pointer lock. Chrome rejects a request made too soon after the
   * user pressed Escape, and refuses entirely in a sandboxed frame, so failure
   * is handled rather than left as an unhandled rejection.
   */
  private capturePointer(): void {
    try {
      const result = this.renderer.canvas.requestPointerLock() as unknown;
      if (result && typeof (result as Promise<void>).catch === 'function') {
        (result as Promise<void>).catch(() => this.onPointerLockUnavailable());
      }
    } catch {
      this.onPointerLockUnavailable();
    }
  }

  private onPointerLockUnavailable(): void {
    // A refusal is often transient — Chrome rejects a request made within about
    // a second of the user pressing Escape — so the request is always retried
    // and only the hint is shown once.
    if (this.pointerLockHintShown) return;
    this.pointerLockHintShown = true;
    this.hud.addChat('Mouse capture unavailable — hold the left mouse button and drag to look around.');
  }

  /** Route a click inside an open window to the slot under the cursor. */
  private handleWindowClick(ev: MouseEvent): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const slot = this.hud.slotAt(ev.clientX * dpr, ev.clientY * dpr);
    const windowId = this.windowState.open ? this.windowState.id : 0;
    const button = ev.button === 2 ? 1 : 0;
    // Clicking outside the panel throws the carried stack on the floor.
    const mode = slot < 0 ? 4 : ev.shiftKey ? 1 : 0;
    this.net.sendInventoryClick(windowId, slot, button, mode);
  }

  private toggleInventory(): void {
    if (this.windowState.open) {
      this.net.sendCloseContainer();
      this.windowState.open = false;
      return;
    }
    this.inventoryOpen = !this.inventoryOpen;
    if (this.inventoryOpen) this.releasePointer();
    else this.capturePointer();
  }

  private releasePointer(): void {
    if (document.pointerLockElement) document.exitPointerLock();
  }

  private selectSlot(slot: number): void {
    if (slot === this.hudState.selectedSlot) return;
    this.hudState.selectedSlot = slot;
    this.net.sendSelectSlot(slot);
    const stack = this.hudState.hotbar[slot];
    const def = stack ? itemById(stack.item) : undefined;
    this.hudState.itemNameFlash = def ? { name: prettyName(def), until: performance.now() + 2000 } : null;
  }

  private setRenderDistance(value: number): void {
    this.renderDistance = Math.max(2, Math.min(24, value));
    localStorage.setItem('opuscraft:renderDistance', String(this.renderDistance));
    this.net.sendRenderDistance(this.renderDistance);
    this.hud.addChat(`Render distance: ${this.renderDistance}`);
  }

  /**
   * The entity under the crosshair, if any. Uses a coarse ray/box test against
   * tracked entities and only accepts hits closer than the block the player is
   * aiming at, so a mob behind a wall is not attackable.
   */
  private pickEntity(): number | null {
    const eyeY = this.player.y + (this.sneaking ? PLAYER_EYE - 0.15 : PLAYER_EYE);
    const yaw = this.player.yaw;
    const pitch = this.player.pitch;
    const dx = -Math.sin(yaw) * Math.cos(pitch);
    const dy = Math.sin(pitch);
    const dz = Math.cos(yaw) * Math.cos(pitch);

    let blockDistance = REACH;
    if (this.target) {
      blockDistance = Math.hypot(
        this.target.hitX - this.player.x,
        this.target.hitY - eyeY,
        this.target.hitZ - this.player.z,
      );
    }

    let bestId: number | null = null;
    let bestDistance = Math.min(REACH, blockDistance);
    for (const e of this.entities.values()) {
      if (e.kind !== 5 && e.kind !== 6) continue; // mobs and players only
      const style = MOB_STYLES[e.type] ?? MOB_STYLES.default!;
      const [w, h] = e.kind === 6 ? [0.6, 1.8] : style.size;
      const hw = w / 2 + 0.15;
      // March along the ray; entity hitboxes are small so this is accurate
      // enough and far cheaper than a proper slab test per entity.
      for (let t = 0.4; t < bestDistance; t += 0.15) {
        const px = this.player.x + dx * t;
        const py = eyeY + dy * t;
        const pz = this.player.z + dz * t;
        if (
          px > e.targetX - hw && px < e.targetX + hw &&
          pz > e.targetZ - hw && pz < e.targetZ + hw &&
          py > e.targetY - 0.15 && py < e.targetY + h + 0.15
        ) {
          bestDistance = t;
          bestId = e.id;
          break;
        }
      }
    }
    return bestId;
  }

  private pickBlock(): void {
    if (!this.target) return;
    const state = this.world.getState(this.target.x, this.target.y, this.target.z);
    const name = blockNameOf(state);
    this.net.sendChat(`/give ${name} 1`);
  }

  // -------------------------------------------------------------------------
  // Simulation
  // -------------------------------------------------------------------------

  private simulate(): void {
    if (this.hudState.dead) {
      this.player.vx = this.player.vy = this.player.vz = 0;
      return;
    }

    const uiOpen = this.inventoryOpen || this.windowState.open || this.hud.chatInput !== null;
    const forward = uiOpen ? 0 : (this.keys.has('KeyW') ? 1 : 0) - (this.keys.has('KeyS') ? 1 : 0);
    const strafe = uiOpen ? 0 : (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0);
    this.sneaking = !uiOpen && this.keys.has('ShiftLeft');
    this.sprinting = !uiOpen && this.keys.has('ControlLeft') && forward > 0;

    const input: MoveInput = {
      strafe,
      forward,
      jump: !uiOpen && this.keys.has('Space'),
      sneak: this.sneaking,
      sprint: this.sprinting,
      yaw: this.player.yaw,
    };

    // Only simulate once the ground under the player actually exists, so the
    // player does not fall through the world while chunks are still streaming.
    const cx = Math.floor(this.player.x) >> 4;
    const cz = Math.floor(this.player.z) >> 4;
    if (this.world.hasChunk(cx, cz)) {
      tickPlayerPhysics(this.world, this.player, input);
    }

    this.net.sendMove(
      this.player.x, this.player.y, this.player.z,
      (this.player.yaw * 180) / Math.PI, (this.player.pitch * 180) / Math.PI,
      this.player.onGround, this.sneaking, this.sprinting,
    );

    this.updateTargeting();
    this.promoteHoldToMining();
    this.updateMining();
    if (this.placeCooldown > 0) this.placeCooldown--;
    this.worldTime = (this.worldTime + 1) % DAY_LENGTH;
  }

  /**
   * In fallback mode a left press that has stayed still long enough stops being
   * a potential look-drag and becomes mining, so the game is fully playable
   * even when the browser refuses to capture the mouse.
   */
  private promoteHoldToMining(): void {
    if (this.pointerLocked || !this.dragLooking || this.dragMoved || this.mining) return;
    if (performance.now() - this.dragStartedAt < DRAG_HOLD_TO_MINE_MS) return;
    const hit = this.pickEntity();
    if (hit !== null) {
      this.net.sendAttack(hit);
      this.dragMoved = true; // consume the press so it does not repeat
      return;
    }
    this.mining = true;
  }

  private updateTargeting(): void {
    const eyeY = this.player.y + (this.sneaking ? PLAYER_EYE - 0.15 : PLAYER_EYE);
    const yaw = this.player.yaw;
    const pitch = this.player.pitch;
    // Must match `lookRotation`: yaw 0 faces +Z, positive pitch looks up.
    const dx = -Math.sin(yaw) * Math.cos(pitch);
    const dy = Math.sin(pitch);
    const dz = Math.cos(yaw) * Math.cos(pitch);
    const hit = raycast(this.world, this.player.x, eyeY, this.player.z, dx, dy, dz, REACH);
    this.target = hit
      ? { x: hit.x, y: hit.y, z: hit.z, face: hit.face, hitX: hit.hitX, hitY: hit.hitY, hitZ: hit.hitZ }
      : null;
  }

  private updateMining(): void {
    if (!this.mining || !this.target || this.inventoryOpen || this.windowState.open) {
      this.stopMining();
      return;
    }
    const { x, y, z } = this.target;
    if (!this.miningTarget || this.miningTarget.x !== x || this.miningTarget.y !== y || this.miningTarget.z !== z) {
      this.miningTarget = { x, y, z };
      this.miningTicks = 0;
      this.net.sendBreakStart(x, y, z);
      return;
    }
    this.miningTicks++;
    const state = this.world.getState(x, y, z);
    const held = this.hudState.hotbar[this.hudState.selectedSlot] ?? null;
    const info = miningInfo(state, held);
    if (info.ticks !== Infinity && this.miningTicks >= info.ticks) {
      this.net.sendBreakFinish(x, y, z);
      this.miningTicks = 0;
      // Let the server's block change confirm; re-arm on the next tick.
      this.miningTarget = null;
    }
  }

  private stopMining(): void {
    if (this.miningTarget) {
      this.net.sendBreakCancel();
      this.miningTarget = null;
      this.miningTicks = 0;
    }
  }

  private usePlace(): void {
    if (this.placeCooldown > 0) return;
    this.placeCooldown = 4;
    if (!this.target) {
      this.net.sendUseItem();
      return;
    }
    const t = this.target;
    this.net.sendPlace(
      t.x, t.y, t.z, t.face,
      t.hitX - t.x, t.hitY - t.y, t.hitZ - t.z,
    );
  }

  // -------------------------------------------------------------------------
  // Meshing
  // -------------------------------------------------------------------------

  private pumpMeshing(): void {
    if (this.world.dirty.size === 0) return;
    const pcx = Math.floor(this.player.x) >> 4;
    const pcz = Math.floor(this.player.z) >> 4;

    // Take the nearest dirty chunks, but never more than the pool can absorb.
    const capacity = this.pool.size * 2 - this.pool.inFlight - this.pool.pending;
    if (capacity <= 0) return;
    const candidates = [...this.world.dirty]
      .map((key) => ({ key, cx: chunkKeyX(key), cz: chunkKeyZ(key) }))
      .filter((c) => Math.abs(c.cx - pcx) <= this.renderDistance + 1 && Math.abs(c.cz - pcz) <= this.renderDistance + 1)
      .sort((a, b) => (a.cx - pcx) ** 2 + (a.cz - pcz) ** 2 - ((b.cx - pcx) ** 2 + (b.cz - pcz) ** 2));

    let submitted = 0;
    for (const c of candidates) {
      if (submitted >= capacity) break;
      // Meshing needs the neighbours for correct seams; wait for them.
      let ready = true;
      for (let dz = -1; dz <= 1 && ready; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (!this.world.hasChunk(c.cx + dx, c.cz + dz)) {
            ready = false;
            break;
          }
        }
      }
      if (!ready) continue;
      const job = this.world.buildJob(c.cx, c.cz, submitted);
      this.world.dirty.delete(c.key);
      if (!job) continue;
      this.pool.submit(job);
      submitted++;
    }
    // Anything outside the render distance stays dirty until the player nears it.
  }

  // -------------------------------------------------------------------------
  // Frame
  // -------------------------------------------------------------------------

  private frame = (now: number): void => {
    const dt = Math.min(100, now - this.lastFrame);
    this.lastFrame = now;
    this.accumulator += dt;

    let steps = 0;
    while (this.accumulator >= MS_PER_TICK && steps < 5) {
      this.accumulator -= MS_PER_TICK;
      this.simulate();
      steps++;
    }
    if (steps === 5) this.accumulator = 0;

    this.pumpMeshing();
    this.renderer.resize();
    this.hud.resize();
    this.render(now, this.accumulator / MS_PER_TICK);

    this.frames++;
    if (now - this.lastFps >= 500) {
      this.fps = (this.frames * 1000) / (now - this.lastFps);
      this.frames = 0;
      this.lastFps = now;
    }
    requestAnimationFrame(this.frame);
  };

  private render(now: number, alpha: number): void {
    const eyeY = this.player.y + (this.sneaking ? PLAYER_EYE - 0.15 : PLAYER_EYE);
    const camX = this.player.x;
    const camY = eyeY;
    const camZ = this.player.z;

    const headState = this.world.getState(Math.floor(camX), Math.floor(camY), Math.floor(camZ));
    const underwater = blockNameOf(headState) === 'water';

    this.renderer.renderDistance = this.renderDistance;
    const biome = this.world.getBiome(Math.floor(camX), Math.floor(camZ));
    const env = this.renderer.environment(this.worldTime, this.dimension, biome);

    this.renderer.beginFrame(camX, camY, camZ, this.player.yaw, this.player.pitch, env, underwater);
    this.renderer.drawTerrain(camX, camY, camZ, env, underwater);
    this.drawEntities(now, env, underwater);

    if (this.target && this.gameMode !== 2) {
      const state = this.world.getState(this.target.x, this.target.y, this.target.z);
      const boxes = collisionBoxes(state);
      this.renderer.drawSelection(
        this.target.x, this.target.y, this.target.z,
        boxes.length > 0 ? boxes : [[0, 0, 0, 1, 1, 1]],
      );
    }

    this.hud.capturePrompt = this.pointerLocked
      ? null
      : this.pointerLockHintShown
        ? 'Mouse capture blocked — drag to look, hold to mine'
        : 'Click to capture the mouse';
    this.updateHud();
    this.hud.draw(this.hudState, this.windowState, this.inventoryOpen, this.inventory);
  }

  private drawEntities(now: number, env: ReturnType<Renderer['environment']>, underwater: boolean): void {
    if (this.entities.size === 0) return;
    this.renderer.beginEntities(this.player.x, this.player.y + PLAYER_EYE, this.player.z, env, underwater);

    for (const e of this.entities.values()) {
      const t = Math.min(1, (now - e.lerpStart) / 100);
      const x = e.prevX + (e.targetX - e.prevX) * t;
      const y = e.prevY + (e.targetY - e.prevY) * t;
      const z = e.prevZ + (e.targetZ - e.prevZ) * t;
      const light = this.lightAt(x, y + 0.5, z, env.brightness, env.ambient);
      const tint: [number, number, number] = e.hurt ? [1.6, 0.5, 0.5] : [1, 1, 1];

      switch (e.kind) {
        case 0: {
          // Item: a small spinning cube of the item's block, or a flat card.
          const def = e.stack ? itemById(e.stack.item) : undefined;
          const spin = (now / 900) % (Math.PI * 2);
          const bob = Math.sin(now / 500) * 0.05;
          let layer = -1;
          if (def?.block) {
            try {
              layer = faceLayer(stateOf(def.block));
            } catch {
              layer = -1;
            }
          }
          this.renderer.drawBox(x, y + bob, z, 0.28, 0.28, 0.28, spin, light, layer >= 0 ? [1, 1, 1] : [0.85, 0.8, 0.6], layer);
          break;
        }
        case 1:
          this.renderer.drawBox(x, y + Math.sin(now / 300) * 0.05, z, 0.22, 0.22, 0.22, now / 400, Math.max(light, 0.8), [0.5, 1, 0.2], -1);
          break;
        case 2:
          this.renderer.drawBox(x, y, z, 1, 1, 1, 0, light, [1, 1, 1], faceLayer(e.blockState));
          break;
        case 3: {
          // Primed TNT flashes white as the fuse runs down.
          const flash = Math.floor(now / 120) % 2 === 0 ? 1.8 : 1.0;
          this.renderer.drawBox(x, y, z, 1, 1, 1, 0, Math.max(light, 0.6), [flash, flash * 0.6, flash * 0.5], faceLayer(stateOf('tnt')));
          break;
        }
        case 4:
          this.renderer.drawBox(x, y, z, 0.12, 0.12, 0.6, e.yaw, light, [0.75, 0.72, 0.68], -1);
          break;
        case 5:
          this.drawMob(e, x, y, z, light, tint);
          break;
        case 6:
          this.drawHumanoid(x, y, z, e.yaw, light, [0.35, 0.55, 0.85]);
          break;
        default:
          break;
      }
    }
    this.renderer.endEntities();
  }

  private drawMob(e: RemoteEntity, x: number, y: number, z: number, light: number, tint: [number, number, number]): void {
    const style = MOB_STYLES[e.type] ?? MOB_STYLES.default!;
    const [w, h, d] = style.size;
    const c = style.color;
    const colour: [number, number, number] = [c[0] * tint[0], c[1] * tint[1], c[2] * tint[2]];
    // Body plus a head cube, which is enough to tell mobs apart at a glance.
    this.renderer.drawBox(x, y, z, w, h * 0.72, d, e.yaw, light, colour, -1);
    this.renderer.drawBox(
      x, y + h * 0.72, z,
      w * 0.72, h * 0.3, d * 0.72, e.yaw, light,
      [colour[0] * 1.12, colour[1] * 1.12, colour[2] * 1.12], -1,
    );
  }

  private drawHumanoid(x: number, y: number, z: number, yaw: number, light: number, colour: [number, number, number]): void {
    this.renderer.drawBox(x, y, z, 0.6, 1.4, 0.35, yaw, light, colour, -1);
    this.renderer.drawBox(x, y + 1.4, z, 0.45, 0.45, 0.45, yaw, light, [0.85, 0.7, 0.6], -1);
  }

  private lightAt(x: number, y: number, z: number, brightness: number, ambient: number): number {
    const bx = Math.floor(x);
    const by = Math.floor(y);
    const bz = Math.floor(z);
    const sky = this.world.getSkyLight(bx, by, bz) / 15;
    const block = this.world.getBlockLight(bx, by, bz) / 15;
    const level = Math.max(sky * brightness, block);
    return ambient + (1 - ambient) * Math.pow(level, 1.35);
  }

  private updateHud(): void {
    if (!this.hud.debugVisible) return;
    const stats = this.renderer.stats;
    const p = this.player;
    const bx = Math.floor(p.x);
    const by = Math.floor(p.y);
    const bz = Math.floor(p.z);
    const state = this.world.getState(bx, by - 1, bz);
    const dayTime = this.worldTime;
    const hours = Math.floor(((dayTime + 6000) % DAY_LENGTH) / 1000);
    const minutes = Math.floor(((((dayTime + 6000) % DAY_LENGTH) % 1000) / 1000) * 60);

    this.hud.debugLines = [
      `opuscraft  ${this.fps.toFixed(0)} fps  ${this.dimension}`,
      `xyz ${p.x.toFixed(2)} ${p.y.toFixed(2)} ${p.z.toFixed(2)}`,
      `chunk ${bx >> 4} ${bz >> 4}  block ${bx & 15} ${by} ${bz & 15}`,
      `facing ${facingName(p.yaw)}  yaw ${((p.yaw * 180) / Math.PI).toFixed(0)}  pitch ${((p.pitch * 180) / Math.PI).toFixed(0)}`,
      `light sky ${this.world.getSkyLight(bx, by, bz)} block ${this.world.getBlockLight(bx, by, bz)}`,
      `standing on ${blockNameOf(state)}`,
      `chunks ${stats.chunksDrawn}/${stats.chunksTotal}  tris ${(stats.triangles / 1000).toFixed(0)}k  draws ${stats.drawCalls}`,
      `mesher ${this.pool.inFlight}/${this.pool.size} busy, ${this.pool.pending} queued, ${this.pool.averageMs.toFixed(1)}ms avg`,
      `dirty ${this.world.dirty.size}  loaded ${this.world.chunks.size}  render distance ${this.renderDistance}`,
      `entities ${this.entities.size}  time ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
      this.connectionLost ? `DISCONNECTED: ${this.connectionLost}` : `net ok  buffered ${(this.net.bufferedAmount / 1024).toFixed(0)}kb`,
    ];
  }
}

/** Texture layer to skin an entity cube with, or -1 for a flat colour. */
function faceLayer(state: number): number {
  if (state <= 0) return -1;
  const render = blockOf(state).render;
  if (render !== 'cube' && render !== 'box') return -1;
  return faceTexture(state, Dir.UP);
}

function facingName(yaw: number): string {
  const deg = (((yaw * 180) / Math.PI) % 360 + 360) % 360;
  if (deg >= 315 || deg < 45) return 'south (+Z)';
  if (deg < 135) return 'west (-X)';
  if (deg < 225) return 'north (-Z)';
  return 'east (+X)';
}

const MOB_STYLES: Record<string, { size: [number, number, number]; color: [number, number, number] }> = {
  zombie: { size: [0.6, 1.95, 0.4], color: [0.29, 0.55, 0.32] },
  skeleton: { size: [0.6, 1.99, 0.4], color: [0.85, 0.85, 0.82] },
  creeper: { size: [0.6, 1.7, 0.6], color: [0.35, 0.78, 0.35] },
  spider: { size: [1.2, 0.9, 1.2], color: [0.25, 0.2, 0.22] },
  enderman: { size: [0.6, 2.9, 0.6], color: [0.08, 0.06, 0.1] },
  blaze: { size: [0.6, 1.8, 0.6], color: [1.0, 0.72, 0.2] },
  cow: { size: [0.9, 1.4, 1.3], color: [0.35, 0.25, 0.18] },
  pig: { size: [0.9, 0.9, 1.2], color: [0.93, 0.6, 0.6] },
  sheep: { size: [0.9, 1.3, 1.2], color: [0.93, 0.93, 0.9] },
  chicken: { size: [0.4, 0.7, 0.5], color: [0.95, 0.95, 0.92] },
  ender_dragon: { size: [4, 3, 8], color: [0.13, 0.1, 0.16] },
  default: { size: [0.7, 1.6, 0.5], color: [0.7, 0.5, 0.7] },
};

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

async function boot(): Promise<void> {
  const canvas = document.getElementById('view') as HTMLCanvasElement;
  const hudCanvas = document.getElementById('hud') as HTMLCanvasElement;
  const loading = document.getElementById('loading')!;
  const errorBox = document.getElementById('error')!;

  try {
    const game = new Game(canvas, hudCanvas);
    const debug = globalThis as unknown as { game: Game; blockName: (s: number) => string };
    debug.game = game;
    // Handy from the devtools console: `blockName(game.world.getState(x, y, z))`.
    debug.blockName = (state: number) => blockNameOf(state);
    await game.start();
    loading.style.display = 'none';
  } catch (err) {
    loading.style.display = 'none';
    errorBox.style.display = 'block';
    errorBox.textContent = `Failed to start: ${(err as Error).message}\n\n${(err as Error).stack ?? ''}`;
    console.error(err);
  }
}

void boot();
